<template lang="pug">
.quantity-picker-per-segment(v-click-outside="hidePopup" ref="quantityPicker")
  BriqInput.quantity-summary(
    data-test-id="quantity-summary"
    :value="inputValue"
    @focus="showPopup"
    :disabled="disabled"
    readonly
    )
  .popup(:class="{ focussed: popupIsShown }" ref="popup" data-test-id="quantity-picker-popup")
    .triangle-part
      .triangle
    .panel
      .quantities-grid
        template(v-for="segment in quantitiesPerSegment" v-if="showSegment(segment.SegmentCode)")
          .segment-label(data-test-id="quantity-picker-segment-label") {{ segment.SegmentName }}
          BriqNumberInputWithButtons(
            :data-test-id="`quantity-input-${kebabSegmentCode(segment)}`"
            type="number"
            step="1"
            min="0"
            :value="segment.Quantity"
            @input="q => changeSegmentQuantity(segment, parseInt(q))"
            :key="segment.SegmentCode"
            @blur="maybeHidePopup"
            :disablePlus="disablePlus"
            :disableInput="disablePlus"
            :readonlyInput="readonlyInput"
            :maxInputAllowed="belongsToDeal && !isCrossSell ? totalQuantityAllowedPerSegment[segment.SegmentCode] : null"
            )
      BriqAlert.mt-S(variant="info" v-if="infoMessage")
        .type-small {{ infoMessage }}
</template>

<script>
import { pipe, path, sortBy, compose, toLower, prop } from 'ramda'
import ClickOutside from 'vue-click-outside'
import BriqNumberInputWithButtons from '@v/components/BriqNumberInputWithButtons.vue'
import toKebab from '@/helpers/toKebab.js'
import { extractSegments } from './DealQuantityPickerHelpers/dealQuantityPickerHelperFunctions.js'

export default {
  name: 'QuantityPickerPerSegment',
  components: { BriqNumberInputWithButtons },
  props: {
    quantitiesPerClassification: { type: Array, required: true },
    disabled: Boolean,
    infoMessage: String,
    disablePlus: Boolean,
    readonlyInput: Boolean,
    belongsToDeal: Boolean,
    isCrossSell: Boolean,
    maxQuantityLeftToBeAdded: Number,
  },
  data() {
    return {
      popupIsShown: false,
    }
  },
  computed: {
    quantitiesPerSegment() {
      const quantitiesPerClassification = this.quantitiesPerClassification
      if (!quantitiesPerClassification || !quantitiesPerClassification.length) return []
      const sortQuantitiesPerSegment = pipe(
        path([0, 'QuantitiesPerSegment']),
        sortBy(prop('Order')),
      )
      return sortQuantitiesPerSegment(this.quantitiesPerClassification)
    },
    totalQuantityAllowedPerSegment() {
      const totalQuantityAllowedPerSegment = {}
      this.quantitiesPerSegment.forEach(x => {
        totalQuantityAllowedPerSegment[x.SegmentCode] = x.Quantity + this.maxQuantityLeftToBeAdded
      })
      return totalQuantityAllowedPerSegment
    },
    inputValue() {
      // todo: refactor to normalizedQuantity
      return this.quantitiesPerSegment.reduce((a, s) => a + s.Quantity, 0)
    },
    allowedSegments() {
      return extractSegments(this.quantitiesPerClassification)
    },
  },
  methods: {
    changeSegmentQuantity(segment, quantity) {
      // This is what was stopping the plus button from working
      // todo: investigate if logic behind totalQuantityAllowedPerSegment is broken, that would also mean a missing unit test
      // const maxQuantity = this.totalQuantityAllowedPerSegment[segment.segmentCode]
      // const newQuantity = quantity > maxQuantity ? maxQuantity : quantity
      const newQuantity = quantity
      segment.Quantity = newQuantity
      this.$emit('changeSegmentQuantity', segment, newQuantity) // emitting because it may need to influence deal booking products
    },
    kebabSegmentCode(segment) {
      return segment?.SegmentCode ? toKebab(segment.SegmentCode) : ''
    },
    hidePopup() {
      this.popupIsShown = false
    },
    showPopup(e) {
      this.$bus.$emit('quantity-picker-pops-up', this)
      this.popupIsShown = true
    },
    maybeHidePopup() {
      const quantityPicker = this.$refs.quantityPicker
      setTimeout(() => {
        if (!quantityPicker.contains(this.$document.activeElement)) this.hidePopup()
      }, 35)
    },
    showSegment(segmentCode) {
      return !this.allowedSegments?.length || this.allowedSegments.includes(segmentCode)
    },
  },
  directives: {
    ClickOutside,
  },
  mounted() {
    this.$bus.$on('quantity-picker-pops-up', component => {
      if (component !== this) this.hidePopup()
    })
  },
}
</script>

<style scoped lang="stylus">
popupOffset = 35px
popupTriangleSize = 6px
halfInputHeight = 19px
triangleOffset = popupOffset - popupTriangleSize + halfInputHeight

.popup
  display none
  grid-template-columns 6px auto
  position absolute
  top -(popupOffset)
  left calc(60px + 11px)
  filter drop-shadow(0 2px 4px rgba(0, 0, 0, .15))
  z-index 100

  .triangle-part
    width 12px
    height 100%
    background transparent
    display block

    .triangle
      position absolute
      top triangleOffset
      border-right popupTriangleSize solid white
      border-left 0px
      border-top popupTriangleSize solid transparent
      border-bottom popupTriangleSize solid transparent
      background cornflower

  .panel
    padding 25px 35px
    background white
    border-radius 3px

  .popup-label
    margin-bottom 6px

.popup.focussed
  display grid

.flex
  display flex

.quantities-grid
  display grid
  grid-template-columns auto 200px
  gap 16px

.segment-label
  display flex
  align-items center

.outside-click
  position fixed
  top 0
  left 0
  right 0
  bottom 0
  opacity .5
  background black
  z-index 100
</style>

<style lang="stylus">
.quantities-grid
  input
    text-align center

.quantity-summary
  input.input[readonly]
    background white !important
  input.input[disabled]
    background #eee !important
</style>
